import React, { useEffect } from "react";
import { Card, Col, Form, Modal, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import FalconCloseButton from "components/common/FalconCloseButton";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import TriageAssessmentCard from "features/preauthorization/components/TriageAssessmentCard";
import VitalSignCard from "features/preauthorization/components/VitalSignCard";
import { useDispatch, useSelector } from "react-redux";
import { handleFillStepData } from "../config/claimSlice";

// Import vital sign icons
import Systolic from "../../../assets/img/blood-pressure.svg";
import Diastolic from "../../../assets/img/blood-pressure-check.svg";
import Weight from "../../../assets/img/sport.png";
import AdmissionWeight from "../../../assets/img/weight-loss.png";
import Height from "../../../assets/img/height.svg";
import Oxygen from "../../../assets/img/oxygen-saturation.svg";
import Respiratory from "../../../assets/img/respiratory-system.svg";
import Pulse from "../../../assets/img/pulse.svg";
import BirthWeight from "../../../assets/img/bathroom-scale.png";
import { ageCalculator } from "helpers/utils";

const EditVitalSignModal = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const { stepFormData, claimRequestData, viewClaimInfo } = useSelector(
    (state) => state.claim
  );
  const patientInfoReq = claimRequestData?.entry?.find(
    (item) => item?.resource?.resourceType === "Patient"
  );
  const birthDate =
    stepFormData?.birth_date || patientInfoReq?.resource?.birthDate;
  const preauthType = viewClaimInfo?.claim_type || stepFormData?.claim_type;

  const { age } = ageCalculator(birthDate);

  const year = age?.years;
  const addmissionBirthCond =
    year < 1 &&
    (preauthType?.claim_types_name || preauthType) === "Institutional";
  const {
    watch,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      temperature: "",
      systolic_blood_pressure: "",
      diastolic_blood_pressure: "",
      pulse: "",
      respiratory_rate: "",
      oxygen_saturation: "",
      weight: "",
      height: "",
      admission_weight: "",
      duration_of_illness: "",
      birth_weight: "",
    },
  });

  // Load initial values from stepFormData when modal opens
  useEffect(() => {
    if (show) {
      reset({
        temperature: stepFormData?.temperature || "",
        systolic_blood_pressure: stepFormData?.systolic_blood_pressure || "",
        diastolic_blood_pressure: stepFormData?.diastolic_blood_pressure || "",
        pulse: stepFormData?.pulse || "",
        respiratory_rate: stepFormData?.respiratory_rate || "",
        oxygen_saturation: stepFormData?.oxygen_saturation || "",
        weight: stepFormData?.weight || "",
        height: stepFormData?.height || "",
        admission_weight: stepFormData?.admission_weight || "",
        duration_of_illness: stepFormData?.duration_of_illness || "",
        birth_weight: stepFormData?.birth_weight || "",
      });
    }
  }, [show, stepFormData, reset]);

  const handleFormSubmit = (data) => {
    dispatch(
      handleFillStepData({ check: "temperature", value: data?.temperature })
    );
    dispatch(
      handleFillStepData({
        check: "systolic_blood_pressure",
        value: data?.systolic_blood_pressure,
      })
    );
    dispatch(
      handleFillStepData({
        check: "diastolic_blood_pressure",
        value: data?.diastolic_blood_pressure,
      })
    );
    dispatch(handleFillStepData({ check: "pulse", value: data?.pulse }));
    dispatch(
      handleFillStepData({
        check: "respiratory_rate",
        value: data?.respiratory_rate,
      })
    );
    dispatch(
      handleFillStepData({
        check: "oxygen_saturation",
        value: data?.oxygen_saturation,
      })
    );
    dispatch(handleFillStepData({ check: "weight", value: data?.weight }));
    dispatch(handleFillStepData({ check: "height", value: data?.height }));
    dispatch(
      handleFillStepData({
        check: "admission_weight",
        value: data?.admission_weight,
      })
    );
    dispatch(
      handleFillStepData({ check: "birth_weight", value: data?.birth_weight })
    );
    dispatch(
      handleFillStepData({
        check: "duration_of_illness",
        value: data?.duration_of_illness,
      })
    );
    dispatch(
      handleFillStepData({
        check: "birth_weight",
        value: data?.birth_weight,
      })
    );

    onHide();
  };

  const handleClose = () => {
    reset({
      temperature: stepFormData?.temperature || "",
      systolic_blood_pressure: stepFormData?.systolic_blood_pressure || "",
      diastolic_blood_pressure: stepFormData?.diastolic_blood_pressure || "",
      pulse: stepFormData?.pulse || "",
      respiratory_rate: stepFormData?.respiratory_rate || "",
      oxygen_saturation: stepFormData?.oxygen_saturation || "",
      weight: stepFormData?.weight || "",
      height: stepFormData?.height || "",
      admission_weight: stepFormData?.admission_weight || "",
      duration_of_illness: stepFormData?.duration_of_illness || "",
      birth_weight: stepFormData?.birth_weight || "",
    });
    onHide();
  };

  return (
    <Modal show={show} size="xl" centered>
      <Modal.Header>
        <Modal.Title className="fs-10">Vital Sign Information</Modal.Title>
        <FalconCloseButton
          onClick={handleClose}
          size="sm"
          className="position-absolute top-0 end-0 me-2 mt-2"
        />
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <Row>
            <Col xs={12} md={4} xl={3} className="mb-2">
              <TriageAssessmentCard
                setValue={setValue}
                watch={watch}
                control={control}
                min={34.5}
                max={46.5}
                name="temperature"
                label="Temperature"
                subLabel="°C"
                required={false}
              />
            </Col>
            <Col xs={12} md={4} xl={3} className="mb-2">
              <VitalSignCard
                setValue={setValue}
                watch={watch}
                label="Systolic B.P"
                imageSrc={Systolic}
                name="systolic_blood_pressure"
                subLabel="mmHg"
                control={control}
                background="badge-subtle-danger"
                min={40}
                max={220}
                required={false}
              />
            </Col>
            <Col xs={12} md={4} xl={3} className="mb-2">
              <VitalSignCard
                setValue={setValue}
                watch={watch}
                imageSrc={Diastolic}
                label="Diastolic B.P"
                name="diastolic_blood_pressure"
                subLabel="mmHg"
                control={control}
                background="badge-subtle-warning"
                min={40}
                max={150}
                required={false}
              />
            </Col>
            <Col xs={12} md={4} xl={3} className="mb-2">
              <VitalSignCard
                setValue={setValue}
                watch={watch}
                imageSrc={Pulse}
                background="badge-subtle-danger"
                label="Pulse"
                name="pulse"
                subLabel="bpm"
                control={control}
                min={40}
                max={200}
                required={false}
              />
            </Col>
            <Col xs={12} md={4} xl={3} className="mb-2">
              <VitalSignCard
                setValue={setValue}
                watch={watch}
                label="Respiratory Rate"
                name="respiratory_rate"
                subLabel="rpm"
                control={control}
                min={10}
                max={70}
                background="badge-subtle-primary"
                imageSrc={Respiratory}
                required={false}
              />
            </Col>
            <Col xs={12} md={4} xl={3} className="mb-2">
              <VitalSignCard
                setValue={setValue}
                watch={watch}
                label="O2 Saturation"
                name="oxygen_saturation"
                imageSrc={Oxygen}
                subLabel="%"
                background="badge-subtle-warning"
                control={control}
                min={0}
                max={100}
                required={false}
              />
            </Col>
            <Col xs={12} md={4} xl={3} className="mb-2">
              <VitalSignCard
                setValue={setValue}
                watch={watch}
                label="Weight"
                name="weight"
                imageSrc={Weight}
                subLabel="kg"
                background="badge-subtle-success"
                control={control}
                min={1}
                max={635.5}
                required={false}
              />
            </Col>
            <Col xs={12} md={4} xl={3} className="mb-2">
              <VitalSignCard
                setValue={setValue}
                watch={watch}
                label="Height"
                name="height"
                subLabel="cm"
                background="badge-subtle-success"
                control={control}
                imageSrc={Height}
                min={1}
                max={272.5}
                required={false}
              />
            </Col>
            <Col xs={12} md={4} xl={3} className="mb-2">
              <VitalSignCard
                setValue={setValue}
                watch={watch}
                label="Admission Weight"
                name="admission_weight"
                imageSrc={AdmissionWeight}
                subLabel="kg"
                background="badge-subtle-info"
                control={control}
                min={1}
                max={635.5}
                required={false}
              />
            </Col>
            <Col xs={12} md={4} xl={3} className="mb-2">
              <VitalSignCard
                setValue={setValue}
                watch={watch}
                label={"Birth Weight"}
                name="birth_weight"
                imageSrc={BirthWeight}
                subLabel={"KG"}
                required={addmissionBirthCond}
                background={"badge-subtle-primary"}
                control={control}
                min={1}
                max={635.5}
              />
            </Col>
          </Row>
          <div className="d-flex justify-content-end mt-4">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Changes
            </button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditVitalSignModal;
